// packages/web-ui/src/AppShell/ContextPanel-modes/ModeChrome.tsx
//
// W2-S3 — common chrome shared by every populated context-panel mode (Chat,
// Files, KB, Views, Diff). UX §5.7 specifies:
//   - 32 px header (project / mode label + optional `[+ New X]` CTA)
//   - 32 px sticky search input (debounced 150 ms; clears with Esc)
//   - 4 px right-edge resize handle (already on `<ContextPanel>` from W2-S2)
//
// The search input wiring is a LOCAL `useState` only — no API calls. The CTA
// is a `<button>` that no-ops; downstream stories wire the actual handler.

import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useReducedMotion } from '../useReducedMotion.js';
import type { ContextPanelStatus } from './useContextPanelStatus.js';

export interface ModeChromeProps {
  modeKey: 'chat' | 'files' | 'kb' | 'views' | 'diff' | 'git';
  testId: string;
  /** Header label (e.g., "Chat", "KB Browser"). */
  title: string;
  /** Optional `[+ New X]` CTA — omit to skip. */
  ctaLabel?: string;
  /** Optional click handler for the CTA — no-op when omitted. */
  onCtaClick?: () => void;
  /** Optional filter-chip strip rendered between header and search input. */
  filters?: ReactNode;
  /** Empty-state body — shown when status === 'idle'. */
  emptyStateText: string;
  /** Error copy — shown when status === 'error'. */
  errorText?: string;
  status: ContextPanelStatus;
  /**
   * Optional populated body slot. Rendered when `status === 'ready'` AND
   * `children` is provided; otherwise the empty-state copy stays in charge
   * of the body region. Keeps backwards-compat with modes that pre-date
   * the slot (they pass no children and continue to render the empty state
   * for both `'idle'` and `'ready'`).
   */
  children?: ReactNode;
}

export function ModeChrome({
  testId,
  title,
  ctaLabel,
  onCtaClick,
  filters,
  emptyStateText,
  errorText,
  status,
  children,
}: ModeChromeProps): JSX.Element {
  const reduced = useReducedMotion();
  const [search, setSearch] = useState('');

  return (
    <div
      data-testid={testId}
      data-status={status}
      className="flex h-full flex-col bg-cream-100"
    >
      <header
        data-testid={`${testId}-header`}
        className="flex h-8 items-center justify-between border-b border-taupe-300 px-2 text-sm font-semibold"
      >
        <span>{title}</span>
        {ctaLabel !== undefined ? (
          <button
            type="button"
            data-testid={`${testId}-cta`}
            className="rounded px-2 py-0.5 text-xs hover:bg-cream-200"
            onClick={onCtaClick ?? (() => undefined)}
          >
            {ctaLabel}
          </button>
        ) : null}
      </header>

      {filters !== undefined ? (
        <div
          data-testid={`${testId}-filters`}
          className="flex h-8 items-center gap-1 border-b border-taupe-300 px-2 text-xs"
        >
          {filters}
        </div>
      ) : null}

      <div className="sticky top-0 z-[1] border-b border-taupe-300 bg-cream-100 p-1">
        <input
          type="text"
          data-testid={`${testId}-search`}
          placeholder="Search…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setSearch('');
            }
          }}
          className="h-7 w-full rounded border border-taupe-300 bg-cream-50 px-2 text-xs outline-none focus:border-taupe-700"
        />
      </div>

      <div className="flex-1 overflow-y-auto" data-testid={`${testId}-body`}>
        {status === 'loading' ? (
          <SkeletonRows reduced={reduced} testId={testId} />
        ) : null}
        {status === 'ready' && children !== undefined ? (
          <div data-testid={`${testId}-list`} className="h-full">
            {children}
          </div>
        ) : null}
        {(status === 'idle' || (status === 'ready' && children === undefined)) ? (
          <div
            data-testid={`${testId}-empty`}
            className="flex h-full flex-col items-center justify-center px-4 text-center text-xs text-taupe-700"
          >
            <p>{emptyStateText}</p>
          </div>
        ) : null}
        {status === 'error' ? (
          <div
            data-testid={`${testId}-error`}
            className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-xs"
          >
            <p>{errorText ?? 'Something went wrong.'}</p>
            <button
              type="button"
              data-testid={`${testId}-retry`}
              className="rounded border border-taupe-500 bg-cream-200 px-3 py-1 text-xs"
              onClick={() => undefined}
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SkeletonRows({
  reduced,
  testId,
}: {
  reduced: boolean;
  testId: string;
}): JSX.Element {
  const animate = reduced ? '' : 'animate-pulse';
  return (
    <ul
      data-testid={`${testId}-skeleton`}
      data-reduced={reduced ? 'true' : 'false'}
      className="flex flex-col gap-2 p-3"
    >
      <li className={`h-4 rounded bg-cream-200 ${animate}`} />
      <li className={`h-4 rounded bg-cream-200 ${animate}`} />
      <li className={`h-4 rounded bg-cream-200 ${animate}`} />
    </ul>
  );
}
